import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  useTheme,
  CircularProgress,
  Button,
  TextField,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  ShoppingCart as OrderIcon,
} from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ReplayIcon from "@mui/icons-material/Replay";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import BlockIcon from "@mui/icons-material/Block";
import { useDispatch } from "react-redux";
import { getFlipkartStatistics } from "app/redux/slice/flipkartSlice";
import { useSnackbar } from "notistack";
import Loader from "@jumbo/components/Loader";
import { LocalizationProvider, DateRangePicker } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import { useNavigate } from "react-router-dom";
import { flipkartStatisticsList } from "app/routes/constant";
import moment from "moment";

const FlipkartStatistics = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const [statusBreakdown, setStatusBreakdown] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);
  const [totalOrder, setTotalOrder] = useState(null);

  useEffect(() => {
    fetchStatistics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApplyFilter = () => {
    setLoading(true);
    if (dateRange[0] === null || dateRange[1] === null) {
      enqueueSnackbar("Please select both start and end date", {
        variant: "error",
      });
      setLoading(false);
      return;
    }
    const params = {
      fromDate: moment(dateRange[0]).format("YYYY-MM-DD"),
      toDate: moment(dateRange[1]).format("YYYY-MM-DD"),
    };
    dispatch(getFlipkartStatistics(params))
      .unwrap()
      .then((res) => {
        if (res?.status === 200) {
          processStatisticsData(res?.data?.data);
        } else {
          enqueueSnackbar("Failed to fetch statistics", { variant: "error" });
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        enqueueSnackbar("Failed to fetch statistics", { variant: "error" });
      });
  };

  const fetchStatistics = () => {
    setLoading(true);
    setDateRange([null, null]);
    dispatch(getFlipkartStatistics({}))
      .unwrap()
      .then((res) => {
        if (res?.status === 200) {
          processStatisticsData(res?.data?.data);
        } else {
          enqueueSnackbar("Failed to fetch statistics", { variant: "error" });
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        enqueueSnackbar("Failed to fetch statistics", { variant: "error" });
      });
  };

  const processStatisticsData = (data) => {
    setTotalOrder(data?.totalOrders);
    const breakdown = data?.statusBreakdown || [];
    setStatusBreakdown(breakdown);
  };

  const getStatusIcon = (status) => {
  const map = {
    DELIVERED: <CheckCircleIcon color="success" />,
    CANCELLED: <CancelIcon color="error" />,
    RETURNED: <ReplayIcon color="warning" />,
    RETURN_REQUESTED: <AssignmentReturnIcon color="info" />,
    IN_TRANSIT: <LocalShippingIcon color="primary" />,
    RETURN_CANCELLED: <BlockIcon color="secondary" />,
  };

  return map[status] || <OrderIcon />;
};

const getStatusColor = (status) => {
  const map = {
    DELIVERED: theme.palette.success.main,
    CANCELLED: theme.palette.error.main,
    RETURNED: theme.palette.warning.main,
    RETURN_REQUESTED: theme.palette.info.main,
    IN_TRANSIT: theme.palette.primary.main,
    RETURN_CANCELLED: theme.palette.secondary.main,
  };

  return map[status] || theme.palette.grey[500];
};

const formatStatusLabel = (status) => {
  const map = {
    DELIVERED: "Delivered",
    CANCELLED: "Cancelled",
    RETURNED: "Returned",
    RETURN_REQUESTED: "Return Requested",
    IN_TRANSIT: "In Transit",
    RETURN_CANCELLED: "Return Cancelled",
  };

  return map[status] || status.replace(/_/g, " ");
};

  const StatusCard = ({ status }) => (
    <Paper
      sx={{
        p: 2,
        textAlign: "center",
        cursor: "pointer",
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
        },
      }}
      onClick={() => {
        let params = null;
        if (dateRange[0] !== null && dateRange[1] !== null) {
          params = {
            fromDate: moment(dateRange[0]).format("YYYY-MM-DD"),
            toDate: moment(dateRange[1]).format("YYYY-MM-DD"),
          };
        }
        navigate(flipkartStatisticsList(status?._id), { state: params });
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 1,
        }}
      >
        <Box sx={{ mr: 1, color: getStatusColor(status._id) }}>
          {getStatusIcon(status._id)}
        </Box>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
          {formatStatusLabel(status._id)}
        </Typography>
      </Box>
      <Typography variant="h4" color={getStatusColor(status._id)}>
        {status.orderCount ?? status.count}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {status.percentage}% of total
      </Typography>
    </Paper>
  );

  return (
    <Box>
      <Typography variant="h4" component="h1" mb={2}>
        Flipkart Statistics
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={5}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateRangePicker
                  startText="From Date"
                  endText="To Date"
                  inputFormat="dd/MM/yyyy"
                  value={dateRange}
                  onChange={setDateRange}
                  renderInput={({ inputProps, ...startProps }, endProps) => {
                    const startValue = inputProps.value;
                    delete inputProps.value;
                    return (
                      <TextField
                        {...startProps}
                        inputProps={inputProps}
                        value={`${startValue || "DD/MM/YYYY"} - ${
                          endProps.inputProps.value || "DD/MM/YYYY"
                        }`}
                        fullWidth
                        size="small"
                      />
                    );
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                variant="contained"
                onClick={handleApplyFilter}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} /> : null}
                fullWidth
              >
                Apply
              </Button>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                variant="outlined"
                onClick={fetchStatistics}
                disabled={loading}
                startIcon={<RefreshIcon />}
                fullWidth
              >
                Reset
              </Button>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant="h5">
                Total Orders: {totalOrder ?? 0}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading ? (
        <Loader />
      ) : (
        <Grid container spacing={3}>
          {statusBreakdown?.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="h5" gutterBottom sx={{ mt: 2 }}>
                Status Breakdown
              </Typography>
              <Grid container spacing={3}>
                {statusBreakdown.map((status) => (
                  <Grid item xs={12} sm={6} md={3} key={status?._id}>
                    <StatusCard status={status} />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
};

export default FlipkartStatistics;
